import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { auth, UNAUTHENTICATED_EVENT, type SessionUser } from './api';

interface SessionState {
  /** Undefined while the first `/me` is still in flight. */
  user: SessionUser | null | undefined;
  /** True when the server runs without accounts, as the desktop shell does. */
  authDisabled: boolean;
  signupOpen: boolean;
  /** Closed, but listed addresses may sign up without an invitation. */
  signupByList: boolean;
  /** The account is suspended: it can sign in and read settings, nothing else. */
  suspended: boolean;
  /** This server holds rendering until the address is confirmed. */
  verificationRequired: boolean;
  setUser: (user: SessionUser | null) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState>({
  user: undefined,
  authDisabled: false,
  signupOpen: true,
  signupByList: false,
  suspended: false,
  verificationRequired: false,
  setUser: () => {},
  signOut: async () => {},
  refresh: async () => {},
});

export const useSession = () => useContext(SessionContext);

/**
 * Who is signed in, asked once on load.
 *
 * The distinction between "not signed in" (null) and "not asked yet"
 * (undefined) matters: without it the app flashes the sign-in page for a
 * moment on every reload before the answer arrives.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);
  const [authDisabled, setAuthDisabled] = useState(false);
  const [signupOpen, setSignupOpen] = useState(true);
  const [signupByList, setSignupByList] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const result = await auth.me();
      setUser(result.user);
      setAuthDisabled(Boolean(result.authDisabled));
      setSignupOpen(result.signupOpen ?? true);
      setSignupByList(Boolean(result.signupByList));
      setSuspended(Boolean(result.suspended));
      setVerificationRequired(Boolean(result.verificationRequired));
    } catch {
      // A server that cannot be reached is not the same as being signed out,
      // but there is nothing useful to render either way.
      setUser(null);
    }
  }, []);

  useEffect(() => {
    /*
     * A client on a review link has no account and never will. Asking the
     * server who they are would cost a round trip and answer 401, which is
     * noise on a page that has nothing to do with sessions.
     */
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/r/')) {
      setUser(null);
      return;
    }
    void refresh();
  }, [refresh]);

  // A 401 from anywhere means the session ended; the sign-in page is the
  // only honest thing to show, and every page would otherwise show an error.
  useEffect(() => {
    const ended = () => setUser((current) => (current ? null : current));
    window.addEventListener(UNAUTHENTICATED_EVENT, ended);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, ended);
  }, []);

  const signOut = useCallback(async () => {
    await auth.logout().catch(() => undefined);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      authDisabled,
      signupOpen,
      signupByList,
      suspended,
      verificationRequired,
      setUser,
      signOut,
      refresh,
    }),
    [user, authDisabled, signupOpen, signupByList, suspended, verificationRequired, signOut, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
